import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { API_BASE } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Users, FileSignature, TrendingUp, RotateCcw, Download, Mail, Send, RefreshCw, ShieldCheck, ShieldAlert, Copy, Trash2, Eraser } from "lucide-react";
import { DataTable, StatCard } from "./_shared";

/**
 * The original "Compliance" landing screen: waitlist + signed agreements
 * + top-level stats. Extracted so /admin/users and /admin/content can
 * live alongside it under the same shell.
 */
export default function AdminCompliance() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [emailTotal, setEmailTotal] = useState(0);
  const [emailPage, setEmailPage] = useState(0);
  const [emailLoading, setEmailLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [domainHealth, setDomainHealth] = useState(null);
  const [dnsLoading, setDnsLoading] = useState(false);

  const EMAIL_PAGE_SIZE = 20;
  const [tab, setTab] = useState("waitlist");
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    api.get(`/admin/waitlist?${p.toString()}`).then(({ data }) => setWaitlist(data)).catch(() => {});
  }, [q]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (tier !== "all") p.set("tier", tier);
    api.get(`/admin/agreements?${p.toString()}`).then(({ data }) => setAgreements(data)).catch(() => {});
  }, [q, tier]);

  const loadEmailLog = async (page = emailPage) => {
    setEmailLoading(true);
    try {
      const offset = page * EMAIL_PAGE_SIZE;
      const { data } = await api.get(`/admin/email-log?limit=${EMAIL_PAGE_SIZE}&offset=${offset}`);
      setEmailLog(data.items || []);
      setEmailTotal(data.total || 0);
      setEmailPage(page);
    } catch { /* silent */ }
    setEmailLoading(false);
  };

  const loadDomainHealth = async () => {
    setDnsLoading(true);
    try {
      const { data } = await api.get("/admin/domain-health");
      setDomainHealth(data);
    } catch { /* silent */ }
    setDnsLoading(false);
  };

  useEffect(() => {
    if (tab === "emails") {
      loadEmailLog(0);
      loadDomainHealth();
    }
  }, [tab]);

  const sendTestEmail = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email first"); return; }
    setSendingTest(true);
    try {
      const { data } = await api.post(`/admin/email-log/test?to=${encodeURIComponent(testEmail.trim())}`);
      if (data.ok) {
        toast.success(`Test email sent → ${data.to}`);
        setTimeout(() => loadEmailLog(0), 800);
      } else {
        toast.error("Send failed — check provider config");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
    setSendingTest(false);
  };

  const reloadWaitlist = async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    const { data } = await api.get(`/admin/waitlist?${p.toString()}`);
    setWaitlist(data);
  };
  const reloadAgreements = async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (tier !== "all") p.set("tier", tier);
    const { data } = await api.get(`/admin/agreements?${p.toString()}`);
    setAgreements(data);
  };
  const reloadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch { /* silent */ }
  };

  const deleteWaitlistRow = async (id, email) => {
    if (!window.confirm(`Delete waitlist entry for ${email}?`)) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      toast.success("Waitlist row deleted");
      reloadWaitlist(); reloadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };
  const deleteAgreementRow = async (id, email) => {
    if (!window.confirm(`Delete signed-agreement row for ${email}? This does not delete the user, only the compliance record.`)) return;
    try {
      await api.delete(`/admin/agreements/${id}`);
      toast.success("Agreement row deleted");
      reloadAgreements(); reloadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };
  const purgeTestData = async () => {
    if (!window.confirm(
      "This will delete every waitlist, agreement, mentor-call, residency-application and seeded-webinar row whose email matches classic test patterns (@example.com, test_wl_*, admin@thesummit.com, alex@example.com). Real member data will NOT be touched. Continue?"
    )) return;
    try {
      const { data } = await api.post("/admin/purge-test-data");
      const total = Object.values(data.purged || {}).reduce((a, b) => a + b, 0);
      toast.success(`Purged ${total} test rows across ${Object.keys(data.purged || {}).length} collections`);
      reloadWaitlist(); reloadAgreements(); reloadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge failed");
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "");
  const csvHref = (path) => `${API_BASE}${path}`;

  const rowDeleteCell = (onClick) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid="admin-row-delete"
      className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-700 transition-colors"
      title="Delete row"
    >
      <Trash2 size={14} />
    </button>
  );

  const waitlistColumns = useMemo(() => [
    { key: "created_at", label: t("admin.cols.when"), render: (r) => fmtDate(r.created_at) },
    { key: "email", label: t("admin.cols.email") },
    { key: "name", label: t("admin.cols.name") },
    { key: "language", label: t("admin.cols.language"), render: (r) => (r.language || "en").toUpperCase() },
    { key: "interest", label: t("admin.cols.interest"), render: (r) => <span className="text-slate-500 truncate max-w-[240px] block" title={r.interest}>{r.interest || "—"}</span> },
    { key: "source", label: t("admin.cols.source"), render: (r) => <Badge variant="secondary" className="uppercase text-[10px]">{r.source}</Badge> },
    { key: "_actions", label: "", render: (r) => rowDeleteCell(() => deleteWaitlistRow(r.id, r.email)) },
  ], [t]);

  const agreementColumns = useMemo(() => [
    { key: "signed_at", label: t("admin.cols.when"), render: (r) => fmtDate(r.signed_at) },
    { key: "user_email", label: t("admin.cols.email") },
    { key: "full_name", label: t("admin.cols.name"), render: (r) => <span className="font-display italic">{r.full_name}</span> },
    { key: "tier", label: t("admin.cols.tier"), render: (r) => <Badge className={r.tier === "premium" ? "bg-blue-800" : "bg-slate-700"}>{r.tier}</Badge> },
    // `whitespace-nowrap` keeps the version tag on one line even in the
    // squeezed admin viewport — otherwise "2026-02-v1" wraps mid-string
    // and looks like a broken column.
    { key: "agreement_version", label: t("admin.cols.version"), render: (r) => <span className="font-mono text-xs text-slate-500 whitespace-nowrap">{r.agreement_version}</span> },
    { key: "ip", label: t("admin.cols.ip"), render: (r) => <span className="font-mono text-xs text-slate-500 whitespace-nowrap">{r.ip}</span> },
    { key: "user_agent", label: t("admin.cols.userAgent"), render: (r) => <span className="text-slate-500 truncate max-w-[220px] block" title={r.user_agent}>{r.user_agent}</span> },
    { key: "_actions", label: "", render: (r) => rowDeleteCell(() => deleteAgreementRow(r.id, r.user_email)) },
  ], [t]);

  const emailColumns = useMemo(() => [
    { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
    { key: "to", label: "To", render: (r) => <span className="font-mono text-[12px]">{r.to}</span> },
    { key: "subject", label: "Subject", render: (r) => <span className="truncate max-w-[300px] block" title={r.subject}>{r.subject}</span> },
    { key: "provider", label: "Provider", render: (r) => (
      <Badge variant="secondary" className="uppercase text-[10px]">{r.provider || "—"}</Badge>
    ) },
    { key: "status", label: "Status", render: (r) => {
      const map = {
        sent:        { c: "bg-emerald-100 text-emerald-800 border-emerald-200", l: "Sent" },
        pending:     { c: "bg-slate-100 text-slate-700 border-slate-200",       l: "Pending" },
        error:       { c: "bg-rose-100 text-rose-800 border-rose-200",          l: "Error" },
        no_provider: { c: "bg-amber-100 text-amber-800 border-amber-200",       l: "No provider" },
      };
      const it = map[r.status] || map.pending;
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${it.c}`}>{it.l}</span>;
    } },
    { key: "error", label: "Detail", render: (r) => <span className="font-mono text-[11px] text-slate-500 truncate max-w-[240px] block" title={r.error || r.provider_id || ""}>{r.error || r.provider_id || "—"}</span> },
  ], []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-black text-4xl text-slate-900 tracking-tight">{t("admin.title")}</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">{t("admin.subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Users size={18} />} label={t("admin.stats.waitlist")} value={stats?.waitlist_total} />
        <StatCard icon={<FileSignature size={18} />} label={t("admin.stats.agreements")} value={stats?.agreements_total} accent />
        <StatCard icon={<TrendingUp size={18} />} label={t("admin.stats.activeMembers")} value={stats?.active_paid_members} />
        <StatCard icon={<RotateCcw size={18} />} label={t("admin.stats.graceRefunds")} value={stats?.grace_refunds} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          data-testid={ADMIN.searchInput}
          placeholder={t("admin.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-11 max-w-md"
        />
        {tab === "agreements" && (
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger data-testid={ADMIN.tierFilter} className="h-11 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin.tierAll")}</SelectItem>
              <SelectItem value="basic">Basic</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
            </SelectContent>
          </Select>
        )}
        <a
          data-testid={tab === "waitlist" ? ADMIN.csvWaitlist : ADMIN.csvAgreements}
          href={csvHref(tab === "waitlist" ? "/admin/waitlist.csv" : "/admin/agreements.csv")}
          className="ml-auto"
        >
          <Button variant="outline" className="border-slate-300 h-11 gap-2">
            <Download size={14} /> {t("admin.downloadCsv")}
          </Button>
        </a>
        <Button
          onClick={purgeTestData}
          variant="outline"
          data-testid="admin-purge-test-data"
          className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 h-11 gap-2"
          title="Delete every row whose email matches classic test patterns"
        >
          <Eraser size={14} /> Purge test data
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="waitlist" data-testid={ADMIN.tabWaitlist}>{t("admin.tabs.waitlist")}</TabsTrigger>
          <TabsTrigger value="agreements" data-testid={ADMIN.tabAgreements}>{t("admin.tabs.agreements")}</TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-tab-emails" className="gap-1.5">
            <Mail size={13} /> Email log
          </TabsTrigger>
        </TabsList>
        <TabsContent value="waitlist" className="mt-4">
          <DataTable empty={t("admin.empty")} columns={waitlistColumns} rows={waitlist} rowTestId={ADMIN.waitlistRow} />
        </TabsContent>
        <TabsContent value="agreements" className="mt-4">
          <DataTable empty={t("admin.empty")} columns={agreementColumns} rows={agreements} rowTestId={ADMIN.agreementRow} />
        </TabsContent>
        <TabsContent value="emails" className="mt-4 space-y-4">
          <DomainHealthPanel data={domainHealth} loading={dnsLoading} onRefresh={loadDomainHealth} />
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg text-slate-900">Live email dispatch log</div>
                <p className="text-sm text-slate-500 mt-1 max-w-xl">
                  Every welcome, agreement, receipt, and admin alert the app fires is written here the moment it's queued. Use the test button to verify the pipeline end-to-end at any time.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  data-testid="admin-email-test-input"
                  className="h-10 w-56"
                />
                <Button
                  onClick={sendTestEmail}
                  disabled={sendingTest}
                  data-testid="admin-email-test-send"
                  className="h-10 bg-blue-800 hover:bg-blue-900 gap-2"
                >
                  <Send size={14} /> {sendingTest ? "Sending…" : "Send test"}
                </Button>
                <Button
                  onClick={loadEmailLog}
                  variant="outline"
                  disabled={emailLoading}
                  data-testid="admin-email-refresh"
                  className="h-10 gap-2"
                >
                  <RefreshCw size={13} className={emailLoading ? "animate-spin" : ""} /> Refresh
                </Button>
              </div>
            </div>
          </div>
          <DataTable
            empty="No emails logged yet."
            columns={emailColumns}
            rows={emailLog}
            rowTestId="admin-email-row"
          />
          {emailTotal > EMAIL_PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-slate-600 pt-2">
              <div data-testid="admin-email-log-count">
                Showing <b>{emailPage * EMAIL_PAGE_SIZE + 1}</b>–<b>{Math.min((emailPage + 1) * EMAIL_PAGE_SIZE, emailTotal)}</b> of <b>{emailTotal}</b>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  data-testid="admin-email-log-prev"
                  disabled={emailPage === 0 || emailLoading}
                  onClick={() => loadEmailLog(emailPage - 1)}
                  className="h-9"
                >
                  ← Prev
                </Button>
                <Button
                  variant="outline"
                  data-testid="admin-email-log-next"
                  disabled={(emailPage + 1) * EMAIL_PAGE_SIZE >= emailTotal || emailLoading}
                  onClick={() => loadEmailLog(emailPage + 1)}
                  className="h-9"
                >
                  Next →
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DomainHealthPanel({ data, loading, onRefresh }) {
  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500 flex items-center gap-2" data-testid="admin-domain-health-loading">
        <RefreshCw size={14} className="animate-spin" /> Checking sender DNS…
      </div>
    );
  }

  const c = data.checks || {};
  const recs = data.recommendations || [];

  const CheckRow = ({ label, entry }) => (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`admin-dns-${label.toLowerCase()}`}>
      {entry?.ok ? (
        <ShieldCheck size={18} className="text-emerald-600 shrink-0 mt-0.5" />
      ) : (
        <ShieldAlert size={18} className="text-rose-600 shrink-0 mt-0.5" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-display font-bold text-slate-900">{label}</span>
          <span className="text-[11px] font-mono text-slate-400 truncate">{entry?.host}</span>
        </div>
        {entry?.record ? (
          <div className="mt-1 font-mono text-[11px] text-slate-600 bg-slate-50 rounded px-2 py-1 break-all">
            {entry.record.length > 240 ? `${entry.record.slice(0, 240)}…` : entry.record}
          </div>
        ) : (
          <div className="mt-1 text-[12px] text-rose-600">No record found.</div>
        )}
        {entry?.parsed?.p && (
          <div className="mt-1 text-[11px] text-slate-500">
            policy=<b className={entry.parsed.p === "none" ? "text-emerald-600" : "text-amber-700"}>{entry.parsed.p}</b>
            {entry.parsed.rua && <> · rua={entry.parsed.rua}</>}
          </div>
        )}
      </div>
    </div>
  );

  const copy = (v) => {
    navigator.clipboard.writeText(v);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-domain-health">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <ShieldCheck size={16} className="text-blue-800" /> Sender domain health
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Live SPF · DKIM · DMARC check for <span className="font-mono text-slate-700">{data.domain}</span>
          </p>
        </div>
        <Button
          onClick={onRefresh}
          variant="outline"
          disabled={loading}
          data-testid="admin-dns-refresh"
          className="h-9 gap-2"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Recheck DNS
        </Button>
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-100 px-4">
        <CheckRow label="SPF" entry={c.spf} />
        <CheckRow label="DKIM" entry={c.dkim} />
        <CheckRow label="DMARC" entry={c.dmarc} />
      </div>

      {recs.length > 0 && (
        <div className="mt-5 space-y-3">
          {recs.map((r, idx) => (
            <div
              key={idx}
              data-testid={`admin-dns-rec-${idx}`}
              className={`rounded-xl border p-4 ${
                r.severity === "error"
                  ? "border-rose-200 bg-rose-50"
                  : "border-amber-200 bg-amber-50"
              }`}
            >
              <div className="flex items-start gap-2">
                <ShieldAlert size={16} className={r.severity === "error" ? "text-rose-700" : "text-amber-700"} />
                <div className="min-w-0 flex-1">
                  <div className={`font-display font-bold text-sm ${r.severity === "error" ? "text-rose-900" : "text-amber-900"}`}>
                    {r.title}
                  </div>
                  <p className="text-xs mt-1 text-slate-700">{r.detail}</p>
                  <div className="mt-3 rounded-lg bg-white border border-slate-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Add this TXT record</div>
                        <div className="text-[11px] text-slate-500 mb-1">
                          Host / name: <span className="font-mono text-slate-800">{r.record_host}</span>
                        </div>
                        <div className="font-mono text-[11px] text-slate-800 break-all">{r.record_value}</div>
                      </div>
                      <Button
                        onClick={() => copy(r.record_value)}
                        variant="outline"
                        size="sm"
                        data-testid={`admin-dns-copy-${idx}`}
                        className="h-8 gap-1 shrink-0"
                      >
                        <Copy size={12} /> Copy
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

